import { useEffect, useRef, useState } from "react";
import AvailabilityCalendar from "./AvailabilityCalendar";
import QuoteRequestForm from "./QuoteRequestForm";
import { fetchAvailability, type DateRange, type Rig } from "../../lib/bookingApi";

interface Props {
  rig: Rig;
  heading: string;
}

const BookingCard = ({ rig, heading }: Props) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [ranges, setRanges] = useState<DateRange[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAvailability(rig)
      .then((next) => {
        if (!cancelled) {
          setRanges(next);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRanges([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rig]);

  function open() {
    dialogRef.current?.showModal();
  }

  function close() {
    dialogRef.current?.close();
  }

  return (
    <>
      <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm">
        <h3 className="font-semibold text-2xl mb-2">{heading}</h3>
        <p className="text-zinc-500 text-sm mb-6">
          Check available dates and tell us about the trip. We’ll email a quote
        </p>
        <button
          type="button"
          onClick={open}
          className="block w-full bg-mars hover:bg-sunset text-white text-center py-6 rounded-2xl font-semibold text-lg transition-all"
        >
          Request a quote
        </button>
      </div>

      <dialog
        ref={dialogRef}
        className="w-[min(72rem,calc(100vw-1.5rem))] max-h-[90vh] overflow-y-auto rounded-3xl p-0 bg-white shadow-2xl backdrop:bg-alt-blk/60"
        onClick={(event) => {
          if (event.target === dialogRef.current) {
            close();
          }
        }}
      >
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h2 className="font-semibold text-2xl">Request a quote</h2>
              <p className="text-zinc-500 text-sm mt-1">
                Pick pickup and return dates, then send a few details.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="text-3xl leading-none text-zinc-500 hover:text-alt-blk px-2"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="lg:grid lg:grid-cols-2 lg:gap-10 lg:items-start">
            <AvailabilityCalendar
              ranges={ranges}
              startDate={startDate}
              endDate={endDate}
              loading={loading}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
            <div className="mt-8 lg:mt-0">
              <QuoteRequestForm rig={rig} startDate={startDate} endDate={endDate} />
            </div>
          </div>
        </div>
      </dialog>
    </>
  );
};

export default BookingCard;
